import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ProfessionalLayout from '../../components/layout/ProfessionalLayout';
import { employeeAPI } from '../../services/api';
import { Loader2, Filter, ArrowRight } from 'lucide-react';
import './MyRecordsPage.css';

const MyRecordsPage = () => {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [filter, setFilter] = useState('all'); // all, published, invited, draft

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      const response = await employeeAPI.getRecords();
      setRecords(response.data || []);
    } catch (error) {
      console.error('Error fetching records:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRecords = records.filter(record => {
    if (filter === 'all') return true;
    const status = (record.status || 'draft').toLowerCase();
    return status === filter;
  });

  const getStatusClass = (status) => {
    const s = (status || 'draft').toLowerCase();
    if (s === 'published' || s === 'verified') return 'pro-status-published';
    if (s === 'invited' || s === 'pending' || s === 'awaiting') return 'pro-status-invited';
    return 'pro-status-draft';
  };

  const getStatusLabel = (status) => {
    const s = (status || 'draft').toLowerCase();
    if (s === 'published' || s === 'verified') return 'Published';
    if (s === 'invited' || s === 'pending' || s === 'awaiting') return 'Invited';
    return 'Draft';
  };

  const formatDateRange = (start, end, isCurrent) => {
    if (!start) return 'Not yet sent';
    const startStr = new Date(start).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    if (isCurrent) return `${startStr}, current`;
    if (!end) return startStr;
    const endStr = new Date(end).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    return `${startStr}, ${endStr}`;
  };

  const renderStars = (rating) => {
    if (!rating) return <span className="records-stars no-rating">AWAITING RATING</span>;
    return (
      <span className="records-stars">
        {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
      </span>
    );
  };

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#4F8EF7]" />
        </div>
      </ProfessionalLayout>
    );
  }

  return (
    <ProfessionalLayout>
      {/* Header */}
      <div className="records-header-row">
        <div>
          <div className="pro-eyebrow">MY RECORDS</div>
          <h1 className="pro-page-title">Every record tied to your VETT ID.</h1>
          <p className="pro-page-sub">
            The complete history, not just what fits on your dashboard. Each row opens the full lifecycle and detail view.
          </p>
        </div>
        <Link to="/professional/verification-requests" className="pro-btn pro-btn-primary">
          Track verification requests <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Filter Bar */}
      <div className="records-filter-bar">
        <Filter className="w-4 h-4 text-[var(--pro-light-text-muted)]" />
        <button 
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
          data-testid="filter-all"
        >
          All ({records.length})
        </button>
        <button 
          className={`filter-btn ${filter === 'published' ? 'active' : ''}`}
          onClick={() => setFilter('published')}
          data-testid="filter-published"
        >
          Published ({records.filter(r => ['published', 'verified'].includes((r.status || '').toLowerCase())).length})
        </button>
        <button 
          className={`filter-btn ${filter === 'invited' ? 'active' : ''}`}
          onClick={() => setFilter('invited')}
          data-testid="filter-invited"
        >
          Invited ({records.filter(r => ['invited', 'pending', 'awaiting'].includes((r.status || '').toLowerCase())).length})
        </button>
        <button 
          className={`filter-btn ${filter === 'draft' ? 'active' : ''}`}
          onClick={() => setFilter('draft')}
          data-testid="filter-draft"
        >
          Draft ({records.filter(r => !r.status || r.status.toLowerCase() === 'draft').length})
        </button>
      </div>

      {/* Records Table */}
      <div className="records-table-wrap" data-testid="records-table">
        <div className="records-table-head">
          <div>Company / role</div>
          <div>Employment type</div>
          <div>Dates</div>
          <div>Record ID</div>
          <div>Rating</div>
          <div>Status</div>
        </div>

        {filteredRecords.length === 0 ? (
          <div className="records-empty">
            <p>No records found{filter !== 'all' ? ` with status "${filter}"` : ''}.</p>
            <Link to="/professional/request-record" className="pro-btn pro-btn-primary" style={{ marginTop: '16px' }}>
              Request your first record
            </Link>
          </div>
        ) : (
          filteredRecords.map((record) => (
            <Link 
              key={record.id} 
              to={`/professional/records/${record.id}`} 
              className="records-table-row"
              data-testid={`record-row-${record.id}`}
            >
              <div>
                <div className="records-company">{record.company_name || record.employer_name || record.company || 'Unknown Company'}</div>
                <div className="records-role">{record.job_title || record.role || 'Unknown Role'}</div>
              </div>
              <div className="records-cell">{record.employment_type || 'Full-time'}</div>
              <div className="records-cell">{formatDateRange(record.start_date, record.end_date, record.is_current)}</div>
              <div className="records-cell mono">{record.record_id || `REC-${record.id?.slice(0, 6).toUpperCase()}`}</div>
              <div>{renderStars(record.performance_rating)}</div>
              <div>
                <span className={`pro-status-badge ${getStatusClass(record.status)}`}>
                  {getStatusLabel(record.status)}
                </span>
              </div>
            </Link>
          ))
        )}
      </div>

      {/* CTA Strip */}
      <div className="pro-cta-strip" style={{ marginTop: '32px' }}>
        <div>
          <div className="pro-cta-strip-title">Add another employer to your history.</div>
          <div className="pro-cta-strip-desc">
            Every co-signed record raises your VETT Score and strengthens your Passport.
          </div>
        </div>
        <Link to="/professional/request-record" className="pro-btn pro-btn-primary">
          Request a new record →
        </Link>
      </div>
    </ProfessionalLayout>
  );
};

export default MyRecordsPage;
